import { List, Map as MapIcon, Search as SearchIcon } from "lucide-react";
import {
  type EntityLinkType,
  type GraphEdge,
  type GraphNode,
  type GraphNodeType,
  type GraphResult,
  type GraphRootReference,
  type WorkspaceProjection,
} from "@mind/contracts/browser";
import type { TFunction } from "i18next";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { forwardRef, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { ApiError } from "../../api/client.js";
import { useAppSession } from "../../app/AppSession.js";
import {
  createEntityLinkCommand,
  createEntityUnlinkCommand,
  GRAPH_MUTABLE_EDGE_TYPES,
  graphApi,
  type CreateEntityLinkCommand,
  type RemoveEntityLinkCommand,
  type GraphTraversalInput,
} from "./graphApi.js";
import { graphNodeKey, graphEndpointKey, layoutGraph, type GraphLayout, type GraphLayoutNode } from "./layout.js";
import { graphQueryKeys } from "./queryKeys.js";
import {
  validateGraphSearch,
  withGraphCursor,
  withGraphRoot,
  withGraphView,
  type GraphSearch,
  type GraphView,
} from "./searchState.js";
import { activeFocusSearch } from "../focus/searchState.js";
import { withDocument } from "../documents/searchState.js";
import { withWorkProject, withWorkTask } from "../work/searchState.js";
import { createExampleGraph } from "./example.js";
import styles from "./GraphScreen.module.css";

const EMPTY_TRAVERSAL: GraphTraversalInput = {
  workspaceId: "",
  root: { type: "note", id: "00000000-0000-7000-8000-000000000000", revision: "0" },
  includeArchived: false,
};
type LinkMutationState = "idle" | "pending" | "success" | "stale" | "forbidden" | "error";

function classifyLinkMutationError(error: unknown): LinkMutationState {
  if (error instanceof ApiError) {
    if (error.code === "STALE_REVISION") return "stale";
    if (error.code === "FORBIDDEN") return "forbidden";
  }
  return "error";
}

export function GraphScreen() {
  const { t } = useTranslation();
  const session = useAppSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeSearch = useRouterState({ select: (state) => state.location.search });
  const search = validateGraphSearch(routeSearch);
  const workspace = selectWorkspace(session.workspaces, session.selectedWorkspace, search.workspace);
  const workspaceId = workspace?.id ?? "";
  const [exampleMode, setExampleMode] = useState(false);
  const exampleGraph = useMemo(() => createExampleGraph(workspaceId, {
    project: t("graph.exampleProject"),
    task: t("graph.exampleTask"),
    document: t("graph.exampleDocument"),
    focusThread: t("graph.exampleFocusThread"),
    account: t("graph.exampleAccount"),
  }), [t, workspaceId]);
  const root = toRootReference(search);
  const rootKey = root === null ? null : graphNodeKey(root);
  const traversalInput = useMemo<GraphTraversalInput | null>(() => {
    if (workspaceId.length === 0 || root === null) return null;
    return {
      workspaceId,
      root,
      ...(search.project === undefined ? {} : { projectId: search.project }),
      ...(search.account === undefined ? {} : { accountId: search.account }),
      ...(search.tag === undefined ? {} : { tag: search.tag }),
      includeArchived: search.includeArchived ?? false,
      ...(search.cursor === undefined ? {} : { cursor: search.cursor }),
      limit: 50,
    };
  }, [root, search.account, search.cursor, search.includeArchived, search.project, search.tag, workspaceId]);

  const [rootQuery, setRootQuery] = useState("");
  const [submittedRootQuery, setSubmittedRootQuery] = useState("");
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(rootKey);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [localError, setLocalError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [linkType, setLinkType] = useState<EntityLinkType>(GRAPH_MUTABLE_EDGE_TYPES[0] ?? "references");
  const [linkSourceKey, setLinkSourceKey] = useState<string>(selectedNodeKey ?? "");
  const [linkTargetKey, setLinkTargetKey] = useState("");
  const [linkTargetQuery, setLinkTargetQuery] = useState("");
  const [linkMutationState, setLinkMutationState] = useState<LinkMutationState>("idle");
  const inspectorRef = useRef<HTMLElement | null>(null);
  const rootSearchRef = useRef<HTMLInputElement | null>(null);
  const pendingRootFocusRef = useRef(false);

  const rootSearchQuery = useInfiniteQuery({
    queryKey: graphQueryKeys.roots(workspaceId, submittedRootQuery),
    enabled: workspaceId.length > 0 && submittedRootQuery.length > 0,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => graphApi.searchRoots(workspaceId, submittedRootQuery, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const targetSearchQuery = useInfiniteQuery({
    queryKey: graphQueryKeys.roots(workspaceId, `target:${linkTargetQuery}`),
    enabled: workspaceId.length > 0 && linkTargetQuery.trim().length > 0,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => graphApi.searchRoots(workspaceId, linkTargetQuery, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });

  const graphQuery = useQuery({
    queryKey: graphQueryKeys.traversal(traversalInput ?? EMPTY_TRAVERSAL),
    enabled: traversalInput !== null,
    queryFn: ({ signal }) => graphApi.traverse(traversalInput ?? EMPTY_TRAVERSAL, signal),
  });
  const graph = graphQuery.data ?? null;
  const nodes = graph?.nodes ?? [];
  const edges = graph?.edges ?? [];
  const nodesByKey = useMemo(() => new Map(nodes.map((node) => [graphNodeKey(node), node])), [nodes]);
  const layout = useMemo<GraphLayout | null>(
    () => (graph === null || rootKey === null ? null : layoutGraph(graph, rootKey)),
    [graph, rootKey],
  );
  const selectedNode = selectedNodeKey === null ? undefined : nodesByKey.get(selectedNodeKey);
  const selectedEdge = selectedEdgeId === null ? undefined : edges.find((edge) => edge.id === selectedEdgeId);
  const rootResults = useMemo(
    () => rootSearchQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [rootSearchQuery.data],
  );
  const targetResults = useMemo(
    () => targetSearchQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [targetSearchQuery.data],
  );
  const selectedTargetNode = nodesByKey.get(linkTargetKey) ?? targetResults.find((node) => graphNodeKey(node) === linkTargetKey);
  const defaultView = useDefaultGraphView();
  const view: GraphView = search.view ?? defaultView;
  const canMutate = workspace?.currentRole !== "viewer" && workspace !== undefined;
  const hasGraph = graph !== null;

  useEffect(() => {
    if (!pendingRootFocusRef.current) return;
    if (graph !== null && !graphQuery.isFetching && !graphQuery.isError) {
      pendingRootFocusRef.current = false;
      requestAnimationFrame(() => inspectorRef.current?.focus({ preventScroll: true }));
      return;
    }
    if (graphQuery.isError) {
      pendingRootFocusRef.current = false;
      requestAnimationFrame(() => rootSearchRef.current?.focus({ preventScroll: true }));
    }
  }, [graph, graphQuery.isError, graphQuery.isFetching, rootKey]);

  const createLinkMutation = useMutation({
    mutationFn: (command: CreateEntityLinkCommand) => graphApi.createLink(command),
    onMutate: () => {
      setLinkMutationState("pending");
      setLinkError(null);
      setLocalError(null);
      setNotice(null);
    },
    onSuccess: async () => {
      setLinkMutationState("success");
      setLinkError(null);
      setNotice(t("graph.linkCreated"));
      setLocalError(null);
      await queryClient.invalidateQueries({ queryKey: graphQueryKeys.all });
    },
    onError: (error) => {
      setLinkMutationState(classifyLinkMutationError(error));
      setNotice(null);
      setLinkError(formatError(error, t("graph.linkError"), t));
    },
  });
  const removeLinkMutation = useMutation({
    mutationFn: (command: RemoveEntityLinkCommand) => graphApi.removeLink(command),
    onMutate: () => {
      setLinkMutationState("pending");
      setLinkError(null);
      setLocalError(null);
      setNotice(null);
    },
    onSuccess: async () => {
      setLinkMutationState("success");
      setLinkError(null);
      setNotice(t("graph.linkRemoved"));
      setLocalError(null);
      setSelectedEdgeId(null);
      await queryClient.invalidateQueries({ queryKey: graphQueryKeys.all });
    },
    onError: (error) => {
      setLinkMutationState(classifyLinkMutationError(error));
      setNotice(null);
      setLinkError(formatError(error, t("graph.linkError"), t));
    },
  });

  useEffect(() => {
    setSelectedNodeKey(rootKey);
    setSelectedEdgeId(null);
    setZoom(1);
  }, [rootKey]);

  useEffect(() => {
    if (selectedNodeKey !== null) {
      setLinkSourceKey(selectedNodeKey);
      const firstTarget = nodes.find((node) => graphNodeKey(node) !== selectedNodeKey);
      setLinkTargetKey(firstTarget === undefined ? "" : graphNodeKey(firstTarget));
    }
  }, [nodes, selectedNodeKey]);

  const selectNode = (node: GraphNode) => {
    setSelectedNodeKey(graphNodeKey(node));
    setSelectedEdgeId(null);
    setNotice(null);
    focusInspector(inspectorRef);
  };
  const selectEdge = (edge: GraphEdge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNodeKey(null);
    setNotice(null);
    focusInspector(inspectorRef);
  };
  const loadRoot = async (nextRoot: GraphRootReference) => {
    const sameRootLoaded = graph !== null && rootKey === graphNodeKey(nextRoot);
    pendingRootFocusRef.current = !sameRootLoaded;
    setLocalError(null);
    setNotice(null);
    setRootQuery("");
    setSubmittedRootQuery("");
    if (sameRootLoaded) focusInspector(inspectorRef);
    await navigate({ to: "/graph", search: withGraphRoot(search, nextRoot) });
  };
  const changeView = async (nextView: GraphView) => {
    await navigate({ to: "/graph", search: withGraphView(search, nextView), replace: true });
  };
  const changeCursor = async (cursor: string | null) => {
    await navigate({ to: "/graph", search: withGraphCursor(search, cursor) });
  };
  const fitGraph = () => {
    setZoom(1);
    setSelectedNodeKey(rootKey);
    setSelectedEdgeId(null);
    focusInspector(inspectorRef);
  };
  const submitRootSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = rootQuery.trim();
    if (query.length === 0) {
      setLocalError(t("graph.chooseRoot"));
      return;
    }
    setLocalError(null);
    setNotice(null);
    setSubmittedRootQuery(query);
  };
  const openNode = (node: GraphNode) => selectNode(node);
  const openEdge = (edge: GraphEdge) => selectEdge(edge);
  const expandNode = async (node: GraphNode) => loadRoot(toRootReference(node));
  const expandEdge = async (edge: GraphEdge) => {
    const current = rootKey;
    const sourceKey = graphEndpointKey(edge.source);
    const targetKey = graphEndpointKey(edge.target);
    const nextEndpoint = current === sourceKey ? edge.target : current === targetKey ? edge.source : edge.target;
    await loadRoot(nextEndpoint);
  };
  const handleNodeKeyDown = (event: KeyboardEvent<SVGGElement>, node: GraphNode) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openNode(node);
    }
  };
  const handleEdgeKeyDown = (event: KeyboardEvent<SVGPathElement>, edge: GraphEdge) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openEdge(edge);
    }
  };
  const openDestination = async (node: GraphNode) => {
    if (node.type === "project") {
      await navigate({ to: "/work", search: withWorkProject({ workspace: workspaceId }, node.id) });
    } else if (node.type === "task") {
      await navigate({ to: "/work", search: withWorkTask({ workspace: workspaceId }, node.id) });
    } else if (node.type === "document") {
      await navigate({ to: "/documents", search: withDocument({ workspace: workspaceId }, node.id) });
    } else if (node.type === "focus_thread") {
      await navigate({ to: "/focus", search: activeFocusSearch({ workspace: workspaceId }, node.id) });
    }
  };
  const createLink = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const source = nodesByKey.get(linkSourceKey);
    const target = selectedTargetNode;
    if (workspace === undefined || source === undefined || target === undefined) {
      setLinkMutationState("error");
      setLinkError(t("graph.noSelection"));
      return;
    }
    if (source.type === target.type && source.id === target.id) {
      setLinkMutationState("error");
      setLinkError(t("graph.selfLink"));
      return;
    }
    setLinkError(null);
    setLocalError(null);
    setNotice(null);
    createLinkMutation.mutate(createEntityLinkCommand({
      commandId: crypto.randomUUID(),
      linkId: crypto.randomUUID(),
      workspaceId: workspace.id,
      linkType,
      source: toRootReference(source),
      target: toRootReference(target),
      sourceMessageId: null,
    }));
  };
  const removeLink = (edge: GraphEdge | undefined = selectedEdge) => {
    if (workspace === undefined || edge === undefined || edge.linkType === "assigned_to") return;
    removeLinkMutation.mutate(createEntityUnlinkCommand({
      commandId: crypto.randomUUID(),
      linkId: edge.id,
      workspaceId: workspace.id,
      revision: edge.revision,
    }));
  };

  if (workspace === undefined) {
    return (
      <section className={styles.screen} aria-labelledby="graph-title">
        <header className={styles.header}>
          <p className={styles.kicker}>{t("graph.title")}</p>
          <h1 className={styles.title} id="graph-title">{t("graph.title")}</h1>
        </header>
        <p className={styles.status} role="status">{t("graph.noWorkspace")}</p>
      </section>
    );
  }
  if (exampleMode) {
    return <ExampleExperience graph={exampleGraph} t={t} onExit={() => setExampleMode(false)} />;
  }

  return (
    <section className={styles.screen} aria-labelledby="graph-title">
      <header className={styles.header}>
        <p className={styles.kicker}>{workspace.name}</p>
        <h1 className={styles.title} id="graph-title">{t("graph.title")}</h1>
      </header>

      <section className={styles.searchPanel} aria-labelledby="graph-root-search-title">
        <form className={styles.searchForm} onSubmit={submitRootSearch}>
          <label htmlFor="graph-root-search" id="graph-root-search-title">{t("graph.chooseRoot")}</label>
          <div className={styles.searchLine}>
            <input
              ref={rootSearchRef}
              id="graph-root-search"
              value={rootQuery}
              onChange={(event) => setRootQuery(event.target.value)}
              placeholder={t("graph.searchPlaceholder")}
              autoComplete="off"
            />
            <button className={styles.searchButton} type="submit" disabled={rootSearchQuery.isFetching}>
              <SearchIcon aria-hidden="true" size={17} />
              {rootSearchQuery.isFetching ? t("graph.searching") : t("graph.search")}
            </button>
          </div>
        </form>
        {submittedRootQuery.length > 0 && (
          <div className={styles.results} aria-live="polite">
            <div className={styles.resultHeader}>
              <h2>{t("graph.rootResults")}</h2>
            </div>
            {rootSearchQuery.isError ? (
              <div className={styles.error} role="alert">
                <span>{formatError(rootSearchQuery.error, t("graph.rootSearchError"), t)}</span>
                <button className={styles.compactButton} type="button" onClick={() => void rootSearchQuery.refetch()}>{t("graph.retry")}</button>
              </div>
            ) : rootSearchQuery.isFetching && rootResults.length === 0 ? (
              <p className={styles.status} role="status">{t("graph.searching")}</p>
            ) : rootResults.length === 0 ? (
              <p className={styles.status}>{t("graph.noResults", { query: submittedRootQuery })}</p>
            ) : (
              <>
                <ul className={styles.rootResults}>
                  {rootResults.map((node) => (
                    <li key={graphNodeKey(node)}>
                      <button
                        className={styles.rootResult}
                        type="button"
                        onClick={() => void loadRoot(toRootReference(node))}
                        aria-label={`${t("graph.showConnectionsFor")}: ${node.label}, ${formatNodeType(node.type, t)}`}
                      >
                        <span className={styles.rootResultLabel}>{node.label}</span>
                        <span className={styles.rootResultType}>{formatNodeType(node.type, t)}</span>
                        <span className={styles.resultAction}>{t("graph.showConnections")}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                {rootSearchQuery.hasNextPage && (
                  <button className={styles.compactButton} type="button" onClick={() => void rootSearchQuery.fetchNextPage()} disabled={rootSearchQuery.isFetchingNextPage}>
                    {rootSearchQuery.isFetchingNextPage ? t("graph.searching") : t("graph.loadMoreRoots")}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </section>
      {root === null && submittedRootQuery.length === 0 ? (
        <section className={styles.onboarding} aria-labelledby="graph-onboarding-title">
          <div>
            <p className={styles.kicker}>{t("graph.startEyebrow")}</p>
            <h2 id="graph-onboarding-title">{t("graph.startTitle")}</h2>
            <p className={styles.status}>{t("graph.startDescription")}</p>
          </div>
          <div className={styles.onboardingSteps}>
            <article><strong>{t("graph.startSearchTitle")}</strong><span>{t("graph.startSearchDescription")}</span></article>
            <article><strong>{t("graph.startExploreTitle")}</strong><span>{t("graph.startExploreDescription")}</span></article>
            <article><strong>{t("graph.startInspectTitle")}</strong><span>{t("graph.startInspectDescription")}</span></article>
          </div>
          <div className={styles.onboardingLinks}>
            <button className={styles.compactButton} type="button" onClick={() => void navigate({ to: "/work" })}>{t("graph.openWork")}</button>
            <button className={styles.compactButton} type="button" onClick={() => void navigate({ to: "/documents" })}>{t("graph.openDocuments")}</button>
            <button className={styles.compactButton} type="button" onClick={() => void navigate({ to: "/focus" })}>{t("graph.openFocus")}</button>
            <button className={styles.searchButton} type="button" onClick={() => setExampleMode(true)}>{t("graph.tryExample")}</button>
          </div>
        </section>
      ) : null}

      {localError !== null && <p className={styles.error} role="alert">{localError}</p>}
      {notice !== null && <p className={styles.status} role="status">{notice}</p>}
      {graphQuery.isError && <p className={styles.error} role="alert">{formatError(graphQuery.error, t("graph.graphError"), t)}</p>}
      {graphQuery.isPending && root !== null && <p className={styles.status} role="status">{t("graph.searching")}</p>}
      {root !== null && hasGraph ? (
        <>
          <div className={styles.viewActions} role="group" aria-label={t("graph.viewLabel")}>
            <button className={styles.compactButton} type="button" aria-pressed={view === "map"} onClick={() => void changeView("map")}>
              <MapIcon aria-hidden="true" size={17} />
              {t("graph.mapView")}
            </button>
            <button className={styles.compactButton} type="button" aria-pressed={view === "list"} onClick={() => void changeView("list")}>
              <List aria-hidden="true" size={17} />
              {t("graph.listView")}
            </button>
          </div>
          <div className={styles.content}>
            {view === "list" ? (
              <SemanticView graph={graph} selectedNodeKey={selectedNodeKey} selectedEdgeId={selectedEdgeId} onOpenNode={openNode} onOpenEdge={openEdge} onExpandNode={expandNode} onExpandEdge={expandEdge} t={t} />
            ) : (
              <VisualMap layout={layout} zoom={zoom} selectedNodeKey={selectedNodeKey} selectedEdgeId={selectedEdgeId} nodes={nodes} onOpenNode={openNode} onOpenEdge={openEdge} onNodeKeyDown={handleNodeKeyDown} onEdgeKeyDown={handleEdgeKeyDown} onZoomChange={setZoom} onFit={fitGraph} t={t} />
            )}
            <Inspector
              ref={inspectorRef}
              graph={graph}
              selectedNode={selectedNode}
              selectedEdge={selectedEdge}
              nodes={nodes}
              targetResults={targetResults}
              targetQuery={linkTargetQuery}
              targetSearchPending={targetSearchQuery.isFetching}
              linkMutationState={linkMutationState}
              linkError={linkError}
              canMutate={canMutate}
              linkType={linkType}
              linkSourceKey={linkSourceKey}
              linkTargetKey={linkTargetKey}
              linkPending={createLinkMutation.isPending}
              removePending={removeLinkMutation.isPending}
              onLinkTypeChange={setLinkType}
              onLinkSourceChange={setLinkSourceKey}
              onTargetQueryChange={setLinkTargetQuery}
              onLinkTargetChange={setLinkTargetKey}
              onCreateLink={createLink}
              onRemoveLink={removeLink}
              onExpandNode={expandNode}
              onExpandEdge={expandEdge}
              onNavigateNode={openDestination}
              t={t}
            />
          </div>
          <div className={styles.pagination}>
            <p className={styles.status}>{t("graph.pageStatus", { loaded: graph.loadedCount, available: graph.availableCount })}{graph.truncated ? ` · ${t("graph.truncated")}` : ""}</p>
            <button className={styles.compactButton} type="button" onClick={() => void changeCursor(graph.nextPageCursor)} disabled={graph.nextPageCursor === null || graphQuery.isFetching}>
              {t("graph.nextPage")}
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

type Translation = TFunction;

type ExampleExperienceProps = {
  readonly graph: GraphResult;
  readonly t: Translation;
  readonly onExit: () => void;
};

function ExampleExperience({ graph, t, onExit }: ExampleExperienceProps) {
  const initialRootKey = graphNodeKey(graph.nodes[0]!);
  const [rootKey, setRootKey] = useState(initialRootKey);
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(initialRootKey);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [view, setView] = useState<GraphView>(() => (
    typeof window !== "undefined" && window.matchMedia("(max-width: 700px)").matches ? "list" : "map"
  ));
  const inspectorRef = useRef<HTMLElement | null>(null);
  const nodesByKey = useMemo(() => new Map(graph.nodes.map((node) => [graphNodeKey(node), node])), [graph.nodes]);
  const selectedNode = selectedNodeKey === null ? undefined : nodesByKey.get(selectedNodeKey);
  const selectedEdge = selectedEdgeId === null ? undefined : graph.edges.find((edge) => edge.id === selectedEdgeId);
  const layout = useMemo(() => layoutGraph(graph, rootKey), [graph, rootKey]);
  const selectNode = (node: GraphNode) => {
    setSelectedNodeKey(graphNodeKey(node));
    setSelectedEdgeId(null);
    focusInspector(inspectorRef);
  };
  const selectEdge = (edge: GraphEdge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNodeKey(null);
    focusInspector(inspectorRef);
  };
  const expandNode = async (node: GraphNode) => {
    setRootKey(graphNodeKey(node));
    setSelectedNodeKey(graphNodeKey(node));
    setSelectedEdgeId(null);
    focusInspector(inspectorRef);
  };
  const expandEdge = async (edge: GraphEdge) => {
    const sourceKey = graphEndpointKey(edge.source);
    const targetKey = graphEndpointKey(edge.target);
    const next = rootKey === sourceKey ? edge.target : rootKey === targetKey ? edge.source : edge.target;
    await expandNode(nodesByKey.get(graphEndpointKey(next)) ?? graph.nodes[0]!);
  };
  const fitGraph = () => {
    setZoom(1);
    setRootKey(initialRootKey);
    setSelectedNodeKey(initialRootKey);
    setSelectedEdgeId(null);
    focusInspector(inspectorRef);
  };
  const onNodeKeyDown = (event: KeyboardEvent<SVGGElement>, node: GraphNode) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectNode(node);
    }
  };
  const onEdgeKeyDown = (event: KeyboardEvent<SVGPathElement>, edge: GraphEdge) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectEdge(edge);
    }
  };
  return (
    <section className={styles.screen} aria-labelledby="graph-example-title">
      <header className={styles.header}>
        <div>
          <p className={styles.kicker}>{t("graph.exampleEyebrow")}</p>
          <h1 className={styles.title} id="graph-example-title">{t("graph.exampleTitle")}</h1>
          <p className={styles.status}>{t("graph.exampleDescription")}</p>
        </div>
        <button className={styles.compactButton} type="button" onClick={onExit}>{t("graph.returnToWorkspace")}</button>
      </header>
      <p className={styles.exampleNotice} role="status">{t("graph.exampleNotice")}</p>
      <div className={styles.viewActions} role="group" aria-label={t("graph.viewLabel")}>
        <button className={styles.compactButton} type="button" aria-pressed={view === "map"} onClick={() => setView("map")}><MapIcon aria-hidden="true" size={17} />{t("graph.mapView")}</button>
        <button className={styles.compactButton} type="button" aria-pressed={view === "list"} onClick={() => setView("list")}><List aria-hidden="true" size={17} />{t("graph.listView")}</button>
      </div>
      <div className={styles.content}>
        {view === "list" ? (
          <SemanticView graph={graph} selectedNodeKey={selectedNodeKey} selectedEdgeId={selectedEdgeId} onOpenNode={selectNode} onOpenEdge={selectEdge} onExpandNode={expandNode} onExpandEdge={expandEdge} t={t} />
        ) : (
          <VisualMap layout={layout} zoom={zoom} selectedNodeKey={selectedNodeKey} selectedEdgeId={selectedEdgeId} nodes={graph.nodes} onOpenNode={selectNode} onOpenEdge={selectEdge} onNodeKeyDown={onNodeKeyDown} onEdgeKeyDown={onEdgeKeyDown} onZoomChange={setZoom} onFit={fitGraph} t={t} />
        )}
        <Inspector
          ref={inspectorRef}
          graph={graph}
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          nodes={graph.nodes}
          targetResults={[]}
          targetQuery=""
          targetSearchPending={false}
          linkMutationState="idle"
          linkError={null}
          canMutate={false}
          linkType={GRAPH_MUTABLE_EDGE_TYPES[0] ?? "references"}
          linkSourceKey={selectedNodeKey ?? ""}
          linkTargetKey=""
          linkPending={false}
          removePending={false}
          onLinkTypeChange={() => undefined}
          onLinkSourceChange={() => undefined}
          onTargetQueryChange={() => undefined}
          onLinkTargetChange={() => undefined}
          onCreateLink={() => undefined}
          onRemoveLink={() => undefined}
          onExpandNode={expandNode}
          onExpandEdge={expandEdge}
          t={t}
        />
      </div>
      <p className={styles.status}>{t("graph.exampleScope", { count: graph.nodes.length })}</p>
    </section>
  );
}

type VisualMapProps = {
  readonly layout: GraphLayout | null;
  readonly zoom: number;
  readonly selectedNodeKey: string | null;
  readonly selectedEdgeId: string | null;
  readonly nodes: readonly GraphNode[];
  readonly onOpenNode: (node: GraphNode) => void;
  readonly onOpenEdge: (edge: GraphEdge) => void;
  readonly onNodeKeyDown: (event: KeyboardEvent<SVGGElement>, node: GraphNode) => void;
  readonly onEdgeKeyDown: (event: KeyboardEvent<SVGPathElement>, edge: GraphEdge) => void;
  readonly onZoomChange: (zoom: number) => void;
  readonly onFit: () => void;
  readonly t: Translation;
};

function VisualMap({ layout, zoom, selectedNodeKey, selectedEdgeId, nodes, onOpenNode, onOpenEdge, onNodeKeyDown, onEdgeKeyDown, onZoomChange, onFit, t }: VisualMapProps) {
  if (layout === null) return <p className={styles.status}>{t("graph.noNodes")}</p>;
  return (
    <section className={styles.mapPanel} aria-labelledby="graph-map-title">
      <div className={styles.mapHeader}>
        <h2 id="graph-map-title">{t("graph.mapView")}</h2>
        <div className={styles.viewActions}>
          <button className={styles.compactButton} type="button" onClick={() => onZoomChange(Math.min(1.5, Number((zoom + 0.1).toFixed(2))))} disabled={zoom >= 1.5} aria-label={t("graph.zoomIn")}>+</button>
          <button className={styles.compactButton} type="button" onClick={() => onZoomChange(Math.max(0.7, Number((zoom - 0.1).toFixed(2))))} disabled={zoom <= 0.7} aria-label={t("graph.zoomOut")}>−</button>
          <button className={styles.compactButton} type="button" onClick={onFit}>{t("graph.fitGraph")}</button>
        </div>
      </div>
      <div className={styles.mapViewport}>
        <svg className={styles.mapCanvas} viewBox={`0 0 ${layout.width / zoom} ${layout.height / zoom}`} role="group" aria-labelledby="graph-map-title graph-map-desc">
          <desc id="graph-map-desc">{t("graph.semanticView")}</desc>
          <defs>
            <marker id="graph-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="strokeWidth">
              <path d="M 0 0 L 8 4 L 0 8 z" fill="currentColor" />
            </marker>
          </defs>
          {layout.edges.map((item) => (
            <g key={item.edge.id}>
              <path
                className={styles.edge}
                d={item.path}
                markerEnd="url(#graph-arrow)"
                data-selected={item.edge.id === selectedEdgeId}
                aria-pressed={item.edge.id === selectedEdgeId}
                role="button"
                tabIndex={0}
                aria-label={`${t("graph.select")}: ${formatEdge(item.edge, nodes, t)}`}
                onClick={() => onOpenEdge(item.edge)}
                onKeyDown={(event) => onEdgeKeyDown(event, item.edge)}
              />
              <text className={styles.edgeLabel} x={(item.source.x + item.target.x) / 2 + 110} y={(item.source.y + item.target.y) / 2 + 4}>{formatEdgeType(item.edge.linkType, t)}</text>
            </g>
          ))}
          {layout.nodes.map((item) => (
            <GraphNodeShape key={graphNodeKey(item.node)} item={item} selected={graphNodeKey(item.node) === selectedNodeKey} onOpen={onOpenNode} onKeyDown={onNodeKeyDown} t={t} />
          ))}
        </svg>
      </div>
    </section>
  );
}

type GraphNodeShapeProps = {
  readonly item: GraphLayoutNode;
  readonly selected: boolean;
  readonly onOpen: (node: GraphNode) => void;
  readonly onKeyDown: (event: KeyboardEvent<SVGGElement>, node: GraphNode) => void;
  readonly t: Translation;
};

function GraphNodeShape({ item, selected, onOpen, onKeyDown, t }: GraphNodeShapeProps) {
  const lines = wrapLabel(item.node.label);
  return (
    <g
      className={styles.node}
      transform={`translate(${item.x} ${item.y})`}
      data-root={item.root}
      data-selected={selected}
      aria-pressed={selected}
      role="button"
      tabIndex={0}
      aria-label={`${t("graph.select")}: ${item.node.label}, ${formatNodeType(item.node.type, t)}`}
      onClick={() => onOpen(item.node)}
      onKeyDown={(event) => onKeyDown(event, item.node)}
    >
      <title>{item.node.label}</title>
      {nodeShape(item.node.type)}
      <text className={styles.nodeLabel} x="110" y={lines.length > 1 ? "29" : "36"}>
        {lines.map((line, index) => <tspan key={`${line}-${index}`} x="110" dy={index === 0 ? 0 : 17}>{line}</tspan>)}
      </text>
      <text className={styles.nodeType} x="110" y="59">{formatNodeType(item.node.type, t)}</text>
    </g>
  );
}

function nodeShape(type: GraphNodeType) {
  switch (type) {
    case "account":
      return <circle className={styles.nodeShape} cx="110" cy="36" r="34" />;
    case "project":
      return <polygon className={styles.nodeShape} points="110,2 216,20 216,52 110,70 4,52 4,20" />;
    case "focus_thread":
      return <rect className={styles.nodeShape} x="4" y="2" width="212" height="68" rx="30" />;
    default:
      return <rect className={styles.nodeShape} x="4" y="2" width="212" height="68" rx="10" />;
  }
}

type SemanticViewProps = {
  readonly graph: GraphResult;
  readonly selectedNodeKey: string | null;
  readonly selectedEdgeId: string | null;
  readonly onOpenNode: (node: GraphNode) => void;
  readonly onOpenEdge: (edge: GraphEdge) => void;
  readonly onExpandNode: (node: GraphNode) => Promise<void>;
  readonly onExpandEdge: (edge: GraphEdge) => Promise<void>;
  readonly t: Translation;
};

function SemanticView({ graph, selectedNodeKey, selectedEdgeId, onOpenNode, onOpenEdge, onExpandNode, onExpandEdge, t }: SemanticViewProps) {
  return (
    <section className={styles.semanticPanel} aria-labelledby="graph-semantic-title">
      <div className={styles.semanticHeader}>
        <h2 id="graph-semantic-title">{t("graph.semanticView")}</h2>
        <span className={styles.resultMeta}>{t("graph.loaded", { loaded: graph.loadedCount, available: graph.availableCount })}</span>
      </div>
      <div className={styles.itemGroup}>
        <h3 className={styles.itemGroupHeading}>{t("graph.nodes")}</h3>
        {graph.nodes.length === 0 ? (
          <p className={styles.status}>{t("graph.noNodes")}</p>
        ) : (
          <ul className={styles.itemList}>
            {graph.nodes.map((node) => {
              const key = graphNodeKey(node);
              const selected = key === selectedNodeKey;
              return (
                <li key={key}>
                  <div className={styles.itemRow} data-selected={selected}>
                    <button className={styles.itemSelect} type="button" aria-pressed={selected} aria-label={`${t("graph.select")}: ${node.label}, ${formatNodeType(node.type, t)}`} onClick={() => onOpenNode(node)}>
                      <span className={styles.itemCopy}>
                        <strong className={styles.itemLabel}>{node.label}</strong>
                        <span className={styles.itemType}>{formatNodeType(node.type, t)}</span>
                      </span>
                    </button>
                    <button className={styles.itemAction} type="button" onClick={() => void onExpandNode(node)}>{t("graph.showConnections")}</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className={styles.itemGroup}>
        <h3 className={styles.itemGroupHeading}>{t("graph.edges")}</h3>
        {graph.edges.length === 0 ? (
          <p className={styles.status}>{t("graph.noEdges")}</p>
        ) : (
          <ul className={styles.itemList}>
            {graph.edges.map((edge) => {
              const selected = edge.id === selectedEdgeId;
              const label = edgeLabel(edge, graph.nodes, t);
              return (
                <li key={edge.id}>
                  <div className={styles.itemRow} data-selected={selected}>
                    <button className={styles.itemSelect} type="button" aria-pressed={selected} aria-label={`${t("graph.select")}: ${label}, ${formatEdgeType(edge.linkType, t)}`} onClick={() => onOpenEdge(edge)}>
                      <span className={styles.itemCopy}>
                        <strong className={styles.itemLabel}>{label}</strong>
                        <span className={styles.itemType}>{formatEdgeType(edge.linkType, t)}</span>
                      </span>
                    </button>
                    <button className={styles.itemAction} type="button" onClick={() => void onExpandEdge(edge)}>{t("graph.showConnections")}</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

type InspectorProps = {
  readonly graph: GraphResult;
  readonly selectedNode: GraphNode | undefined;
  readonly selectedEdge: GraphEdge | undefined;
  readonly nodes: readonly GraphNode[];
  readonly targetResults: readonly GraphNode[];
  readonly targetQuery: string;
  readonly targetSearchPending: boolean;
  readonly linkMutationState: LinkMutationState;
  readonly linkError: string | null;
  readonly canMutate: boolean;
  readonly linkType: EntityLinkType;
  readonly linkSourceKey: string;
  readonly linkTargetKey: string;
  readonly linkPending: boolean;
  readonly removePending: boolean;
  readonly onLinkTypeChange: (value: EntityLinkType) => void;
  readonly onLinkSourceChange: (value: string) => void;
  readonly onTargetQueryChange: (value: string) => void;
  readonly onLinkTargetChange: (value: string) => void;
  readonly onCreateLink: (event: FormEvent<HTMLFormElement>) => void;
  readonly onRemoveLink: () => void;
  readonly onExpandNode: (node: GraphNode) => Promise<void>;
  readonly onExpandEdge: (edge: GraphEdge) => Promise<void>;
  readonly onNavigateNode?: (node: GraphNode) => Promise<void>;
  readonly t: Translation;
};

const Inspector = forwardRef<HTMLElement, InspectorProps>(function Inspector(
  {
    graph,
    selectedNode,
    selectedEdge,
    nodes,
    targetResults,
    targetQuery,
    targetSearchPending,
    linkMutationState,
    linkError,
    canMutate,
    linkType,
    linkSourceKey,
    linkTargetKey,
    linkPending,
    removePending,
    onLinkTypeChange,
    onLinkSourceChange,
    onLinkTargetChange,
    onCreateLink,
    onTargetQueryChange,
    onRemoveLink,
    onExpandNode,
    onExpandEdge,
    onNavigateNode,
    t,
  },
  ref,
) {
  const selectedTitle = selectedNode === undefined
    ? selectedEdge === undefined ? t("graph.noSelection") : formatEdge(selectedEdge, graph.nodes, t)
    : selectedNode.label;
  const destination = selectedNode === undefined ? undefined : destinationForNode(selectedNode.type);
  return (
    <aside className={styles.inspector} ref={ref} tabIndex={-1} aria-labelledby="graph-inspector-title">
      <div className={styles.inspectorHeader}>
        <h2 id="graph-inspector-title">{selectedTitle}</h2>
        <div className={styles.inspectorActions}>
          {selectedNode !== undefined && <button className={styles.compactButton} type="button" onClick={() => void onExpandNode(selectedNode)}>{t("graph.expand")}</button>}
          {selectedEdge !== undefined && <button className={styles.compactButton} type="button" onClick={() => void onExpandEdge(selectedEdge)}>{t("graph.expand")}</button>}
        </div>
      </div>
      {selectedNode === undefined && selectedEdge === undefined ? (
        <p className={styles.inspectorMeta}>{t("graph.noSelection")}</p>
      ) : selectedNode !== undefined ? (
        <dl className={styles.inspectorDetails}>
          <div><dt>{t("graph.nodeType")}</dt><dd>{formatNodeType(selectedNode.type, t)}</dd></div>
          <div><dt>{t("graph.archived")}</dt><dd>{selectedNode.archived ? t("graph.yes") : t("graph.no")}</dd></div>
        </dl>
      ) : selectedEdge !== undefined ? (
        <dl className={styles.inspectorDetails}>
          <div><dt>{t("graph.edgeType")}</dt><dd>{formatEdgeType(selectedEdge.linkType, t)}</dd></div>
          <div><dt>{t("graph.source")}</dt><dd>{endpointLabel(selectedEdge.source, graph.nodes, t)}</dd></div>
          <div><dt>{t("graph.target")}</dt><dd>{endpointLabel(selectedEdge.target, graph.nodes, t)}</dd></div>
        </dl>
      ) : null}
      {destination !== undefined && selectedNode !== undefined && onNavigateNode !== undefined ? (
        <button className={styles.compactButton} type="button" onClick={() => void onNavigateNode(selectedNode)}>
          {t(`graph.openIn.${destination}`)}
        </button>
      ) : null}
      {canMutate && (
        <details className={styles.editorDisclosure}>
          <summary>{t("graph.relationshipEditor")}</summary>
          <form className={styles.linkForm} onSubmit={onCreateLink}>
            <label>
              {t("graph.linkType")}
              <select value={linkType} onChange={(event) => onLinkTypeChange(event.target.value as EntityLinkType)}>
                {GRAPH_MUTABLE_EDGE_TYPES.map((type) => <option value={type} key={type}>{formatEdgeType(type, t)}</option>)}
              </select>
            </label>
            <label>
              {t("graph.source")}
              <select value={linkSourceKey} onChange={(event) => onLinkSourceChange(event.target.value)}>
                {nodes.map((node) => <option value={graphNodeKey(node)} key={graphNodeKey(node)}>{node.label} · {formatNodeType(node.type, t)}</option>)}
              </select>
            </label>
            <label>
              {t("graph.target")}
              <input
                value={targetQuery}
                onChange={(event) => onTargetQueryChange(event.target.value)}
                placeholder={t("graph.targetSearchPlaceholder")}
                autoComplete="off"
              />
            </label>
            {targetSearchPending && targetQuery.trim().length > 0 && <p className={styles.status} role="status">{t("graph.searching")}</p>}
            {!targetSearchPending && targetQuery.trim().length > 0 && targetResults.length === 0 && (
              <p className={styles.status}>{t("graph.noResults", { query: targetQuery.trim() })}</p>
            )}
            {targetResults.length > 0 && (
              <ul className={styles.rootResults} aria-label={t("graph.rootResults")}>
                {targetResults.map((node) => (
                  <li key={graphNodeKey(node)}>
                    <button
                      className={styles.rootResult}
                      type="button"
                      aria-pressed={linkTargetKey === graphNodeKey(node)}
                      onClick={() => onLinkTargetChange(graphNodeKey(node))}
                    >
                      <span className={styles.rootResultLabel}>{node.label}</span>
                      <span className={styles.rootResultType}>{formatNodeType(node.type, t)}</span>
                      <span className={styles.resultAction}>{t("graph.select")}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button className={styles.searchButton} type="submit" disabled={linkPending || linkTargetKey.length === 0}>
              {linkPending ? t("graph.searching") : t("graph.createLink")}
            </button>
            {linkMutationState === "pending" && <p className={styles.status} role="status">{t("graph.linkPending")}</p>}
            {linkMutationState === "success" && <p className={styles.status} role="status">{t("graph.linkSaved")}</p>}
            {linkMutationState === "stale" && linkError === null && <p className={styles.error} role="alert">{t("graph.linkStale")}</p>}
            {linkMutationState === "forbidden" && linkError === null && <p className={styles.error} role="alert">{t("graph.linkForbidden")}</p>}
            {linkMutationState === "error" && linkError === null && <p className={styles.error} role="alert">{t("graph.linkError")}</p>}
            {linkError !== null && <p className={styles.error} role="alert">{linkError}</p>}
          </form>
        </details>
      )}
      {canMutate && selectedEdge !== undefined && selectedEdge.linkType !== "assigned_to" && (
        <button className={styles.dangerButton} type="button" onClick={onRemoveLink} disabled={removePending}>{removePending ? t("graph.searching") : t("graph.removeLink")}</button>
      )}
    </aside>
  );
});

function focusInspector(ref: { current: HTMLElement | null }) {
  requestAnimationFrame(() => ref.current?.focus({ preventScroll: true }));
}

function useDefaultGraphView(): GraphView {
  const [view, setView] = useState<GraphView>(() => (
    typeof window !== "undefined" && window.matchMedia("(max-width: 700px)").matches ? "list" : "map"
  ));

  useEffect(() => {
    const media = window.matchMedia("(max-width: 700px)");
    const update = () => setView(media.matches ? "list" : "map");
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return view;
}

function selectWorkspace(
  workspaces: readonly WorkspaceProjection[],
  selectedWorkspace: WorkspaceProjection | null,
  requestedWorkspaceId: string | undefined,
): WorkspaceProjection | undefined {
  return workspaces.find((candidate) => candidate.id === requestedWorkspaceId) ?? selectedWorkspace ?? workspaces[0];
}

type GraphDestination = "work" | "documents" | "focus";

function destinationForNode(type: GraphNodeType): GraphDestination | undefined {
  if (type === "project" || type === "task") return "work";
  if (type === "document") return "documents";
  if (type === "focus_thread") return "focus";
  return undefined;
}

function toRootReference(entity: Pick<GraphNode, "type" | "id" | "revision">): GraphRootReference;
function toRootReference(search: GraphSearch): GraphRootReference | null;
function toRootReference(input: GraphSearch | Pick<GraphNode, "type" | "id" | "revision">): GraphRootReference | null {
  if ("type" in input) return { type: input.type, id: input.id, revision: input.revision };
  if (input.rootType === undefined || input.rootId === undefined || input.rootRevision === undefined) return null;
  return { type: input.rootType, id: input.rootId, revision: input.rootRevision };
}

function formatError(error: unknown, fallback: string, t: Translation): string {
  if (error instanceof ApiError) return t(error.messageKey, error.params);
  return fallback;
}

function formatNodeType(type: GraphNodeType, t: Translation): string {
  return t(`graph.nodeTypes.${type}`);
}

function formatEdgeType(type: GraphEdge["linkType"], t: Translation): string {
  return t(`graph.edgeTypes.${type}`);
}

function endpointLabel(endpoint: GraphEdge["source"], nodes: readonly GraphNode[], t: Translation): string {
  const node = nodes.find((candidate) => graphNodeKey(candidate) === graphEndpointKey(endpoint));
  return node?.label ?? formatNodeType(endpoint.type, t);
}

function edgeLabel(edge: GraphEdge, nodes: readonly GraphNode[], t: Translation): string {
  return `${endpointLabel(edge.source, nodes, t)} → ${endpointLabel(edge.target, nodes, t)}`;
}

function formatEdge(edge: GraphEdge, nodes: readonly GraphNode[], t: Translation): string {
  return `${edgeLabel(edge, nodes, t)} · ${formatEdgeType(edge.linkType, t)}`;
}

function wrapLabel(label: string): readonly string[] {
  const normalized = label.trim();
  if (normalized.length <= 24) return [normalized];
  return [
    `${normalized.slice(0, 23).trimEnd()}…`,
    `${normalized.slice(23, 46).trimEnd()}${normalized.length > 46 ? "…" : ""}`,
  ];
}